import { useState, useEffect, useCallback } from "react";
import { BotList } from "./ChessGame";

export const CHECKERS_BOTS = [
  { key: "pino", name: "Nonno Pino", avatar: "🧓", rating: 600, level: "Principiante", depth: 0, coins: 20, desc: "Mangia quando può, ma non guarda avanti" },
  { key: "sofia", name: "Sofia", avatar: "👧", rating: 900, level: "Facile", depth: 1, coins: 30, desc: "Evita le mangiate più ovvie" },
  { key: "marco", name: "Marco", avatar: "🧑‍🎓", rating: 1200, level: "Medio", depth: 2, coins: 40, desc: "Pensa alla tua risposta" },
  { key: "aisha", name: "Aisha", avatar: "👩‍🏫", rating: 1500, level: "Difficile", depth: 4, coins: 55, desc: "Prepara le doppie mangiate" },
  { key: "leo", name: "Gran Maestro Leo", avatar: "🦁", rating: 1800, level: "Esperto", depth: 6, coins: 80, desc: "Vede sei mosse avanti" },
];

const SIZE = 8;

const initBoard = () => {
  const b = Array.from({ length: SIZE }, () => Array(SIZE).fill(null));
  for (let r = 0; r < 3; r++)
    for (let c = 0; c < SIZE; c++)
      if ((r + c) % 2 === 1) b[r][c] = { p: 2, k: false };
  for (let r = 5; r < 8; r++)
    for (let c = 0; c < SIZE; c++)
      if ((r + c) % 2 === 1) b[r][c] = { p: 1, k: false };
  return b;
};

const inside = (r, c) => r >= 0 && r < SIZE && c >= 0 && c < SIZE;

const pieceMoves = (board, r, c) => {
  const piece = board[r][c];
  if (!piece) return { steps: [], jumps: [] };
  const dirs = piece.k ? [[-1, -1], [-1, 1], [1, -1], [1, 1]] : piece.p === 1 ? [[-1, -1], [-1, 1]] : [[1, -1], [1, 1]];
  const steps = [], jumps = [];
  for (const [dr, dc] of dirs) {
    const r1 = r + dr, c1 = c + dc;
    if (!inside(r1, c1)) continue;
    if (!board[r1][c1]) steps.push({ to: [r1, c1] });
    else if (board[r1][c1].p !== piece.p) {
      const r2 = r + dr * 2, c2 = c + dc * 2;
      if (inside(r2, c2) && !board[r2][c2]) jumps.push({ to: [r2, c2], cap: [r1, c1] });
    }
  }
  return { steps, jumps };
};

const allMoves = (board, player) => {
  const steps = [], jumps = [];
  for (let r = 0; r < SIZE; r++)
    for (let c = 0; c < SIZE; c++)
      if (board[r][c]?.p === player) {
        const m = pieceMoves(board, r, c);
        m.steps.forEach(s => steps.push({ from: [r, c], ...s }));
        m.jumps.forEach(j => jumps.push({ from: [r, c], ...j }));
      }
  return jumps.length > 0 ? { moves: jumps, mustCapture: true } : { moves: steps, mustCapture: false };
};

const applyMove = (board, move) => {
  const b = board.map(row => row.map(cell => (cell ? { ...cell } : null)));
  const [fr, fc] = move.from, [tr, tc] = move.to;
  b[tr][tc] = b[fr][fc];
  b[fr][fc] = null;
  if (move.cap) b[move.cap[0]][move.cap[1]] = null;
  let kinged = false;
  if (!b[tr][tc].k && ((b[tr][tc].p === 1 && tr === 0) || (b[tr][tc].p === 2 && tr === SIZE - 1))) {
    b[tr][tc].k = true;
    kinged = true;
  }
  return { board: b, kinged };
};

const evalBoard = (b) => {
  let s = 0;
  for (const row of b) for (const cell of row) if (cell) s += (cell.p === 2 ? 1 : -1) * (cell.k ? 4 : 1);
  return s;
};

const minimax = (b, player, depth, alpha, beta) => {
  const { moves } = allMoves(b, player);
  if (moves.length === 0) return { score: player === 2 ? -100 - depth : 100 + depth };
  if (depth === 0) return { score: evalBoard(b) };
  let best = null;
  if (player === 2) {
    let val = -Infinity;
    for (const m of moves) {
      const { board: nb } = applyMove(b, m);
      const r = minimax(nb, 1, depth - 1, alpha, beta);
      if (r.score > val) { val = r.score; best = m; }
      alpha = Math.max(alpha, val);
      if (beta <= alpha) break;
    }
    return { score: val, move: best };
  }
  let val = Infinity;
  for (const m of moves) {
    const { board: nb } = applyMove(b, m);
    const r = minimax(nb, 2, depth - 1, alpha, beta);
    if (r.score < val) { val = r.score; best = m; }
    beta = Math.min(beta, val);
    if (beta <= alpha) break;
  }
  return { score: val, move: best };
};

export default function CheckersGame({ lang, t, onBack, onFinish, playSound, child, others = [], onVersusFinish }) {
  const [mode, setMode] = useState(null); // "ai" | "local" | "versus"
  const [opponent, setOpponent] = useState(null);
  const [picking, setPicking] = useState(false);
  const [bot, setBot] = useState(CHECKERS_BOTS[0]);
  const [board, setBoard] = useState(initBoard);
  const [turn, setTurn] = useState(1);
  const [selected, setSelected] = useState(null);
  const [chainPiece, setChainPiece] = useState(null);
  const [winner, setWinner] = useState(null);
  const [reward, setReward] = useState(null);
  const [aiThinking, setAiThinking] = useState(false);

  const it = lang === "it";
  const label = (p) => {
    if (p === 1) return mode === "versus" ? (child?.name || "🔴") : (it ? "Rosso" : "Red");
    if (mode === "ai") return `${bot.avatar} ${bot.name}`;
    if (mode === "versus") return opponent?.name || "⚫";
    return it ? "Nero" : "Black";
  };

  const restart = (m, b) => {
    setMode(m);
    if (b) setBot(b);
    setBoard(initBoard());
    setTurn(1);
    setSelected(null);
    setChainPiece(null);
    setWinner(null);
    setReward(null);
  };

  const finishGame = useCallback(async (win) => {
    setWinner(win);
    if (playSound) playSound(win === 1 || mode !== "ai" ? "victory" : "wrong");
    if (mode === "ai" && onFinish) {
      const res = await onFinish(win === 1, bot.key);
      if (res) setReward(res);
    } else if (mode === "versus" && onVersusFinish && opponent) {
      const winnerId = win === 1 ? child.id : opponent.id;
      const loserId = win === 1 ? opponent.id : child.id;
      const winnerName = win === 1 ? child.name : opponent.name;
      const loserName = win === 1 ? opponent.name : child.name;
      const res = await onVersusFinish(winnerId, loserId);
      if (res) setReward({ text: `${winnerName} +${res.coins_winner} 💰 · ${loserName} +${res.coins_loser} 💰` });
    }
  }, [mode, bot, onFinish, playSound, onVersusFinish, opponent, child]);

  const endTurnOrChain = useCallback((b, move, kinged) => {
    if (move.cap && !kinged) {
      const more = pieceMoves(b, move.to[0], move.to[1]).jumps;
      if (more.length > 0) {
        setChainPiece(move.to);
        setSelected(move.to);
        return false;
      }
    }
    setChainPiece(null);
    setSelected(null);
    return true;
  }, []);

  const checkWinner = useCallback((b, nextPlayer) => {
    const { moves } = allMoves(b, nextPlayer);
    const hasPieces = b.some(row => row.some(cell => cell?.p === nextPlayer));
    if (!hasPieces || moves.length === 0) return nextPlayer === 1 ? 2 : 1;
    return null;
  }, []);

  const playMove = useCallback((move) => {
    const { board: b, kinged } = applyMove(board, move);
    setBoard(b);
    if (playSound) {
      playSound(move.cap ? "coins" : "click");
      if (kinged) setTimeout(() => playSound("medal"), 200);
    }
    const turnOver = endTurnOrChain(b, move, kinged);
    if (turnOver) {
      const next = turn === 1 ? 2 : 1;
      const w = checkWinner(b, next);
      if (w) finishGame(w);
      else setTurn(next);
    }
  }, [board, turn, endTurnOrChain, checkWinner, finishGame, playSound]);

  // AI turn
  useEffect(() => {
    if (mode !== "ai" || turn !== 2 || winner) return;
    setAiThinking(true);
    const timer = setTimeout(() => {
      let b = board;
      let mv;
      if (chainPiece) {
        const jumps = pieceMoves(b, chainPiece[0], chainPiece[1]).jumps;
        mv = jumps[Math.floor(Math.random() * jumps.length)];
        mv = { from: chainPiece, ...mv };
      } else if (bot.depth > 0) {
        mv = minimax(b, 2, bot.depth, -Infinity, Infinity).move;
        if (!mv) {
          const { moves } = allMoves(b, 2);
          mv = moves[Math.floor(Math.random() * moves.length)];
        }
      } else {
        const { moves } = allMoves(b, 2);
        const scored = moves.map(m => {
          let score = Math.random();
          if (m.cap) score += 10;
          if (!b[m.from[0]][m.from[1]].k && m.to[0] === SIZE - 1) score += 5;
          return { m, score };
        });
        scored.sort((a, x) => x.score - a.score);
        mv = scored[0]?.m;
      }
      setAiThinking(false);
      if (mv) playMove(mv);
    }, 700);
    return () => clearTimeout(timer);
  }, [mode, turn, winner, board, chainPiece, bot, playMove]);

  const handleCell = (r, c) => {
    if (winner || (mode === "ai" && turn === 2)) return;
    const cell = board[r][c];
    const { moves, mustCapture } = allMoves(board, turn);
    if (cell?.p === turn) {
      if (chainPiece && (chainPiece[0] !== r || chainPiece[1] !== c)) return;
      const own = moves.filter(m => m.from[0] === r && m.from[1] === c);
      if (own.length === 0) return;
      setSelected([r, c]);
      return;
    }
    if (!selected) return;
    const candidates = chainPiece
      ? pieceMoves(board, chainPiece[0], chainPiece[1]).jumps.map(j => ({ from: chainPiece, ...j }))
      : moves.filter(m => m.from[0] === selected[0] && m.from[1] === selected[1] && (!mustCapture || m.cap));
    const mv = candidates.find(m => m.to[0] === r && m.to[1] === c);
    if (mv) playMove(mv);
  };

  const legalTargets = (() => {
    if (!selected || winner) return [];
    if (chainPiece) return pieceMoves(board, chainPiece[0], chainPiece[1]).jumps.map(j => j.to);
    const { moves } = allMoves(board, turn);
    return moves.filter(m => m.from[0] === selected[0] && m.from[1] === selected[1]).map(m => m.to);
  })();

  if (!mode) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-800 via-stone-700 to-amber-900 p-4">
        <div className="max-w-md w-full mx-auto">
          <button onClick={onBack} className="bg-white/20 text-white px-4 py-2 rounded-full font-bold hover:bg-white/30 mb-4" data-testid="checkers-back-btn">← {t.backHome}</button>
          <div className="text-center text-white mb-4">
            <div className="text-6xl mb-2">🔴⚫</div>
            <h2 className="text-3xl font-bold">{it ? "Dama" : "Checkers"}</h2>
            <p className="text-white/70 text-sm mt-1" data-testid="checkers-rating">{it ? "Il tuo punteggio Dama" : "Your Checkers rating"}: <b className="text-amber-300">{child?.checkers_rating ?? 800}</b></p>
          </div>
          <div className="bg-white rounded-3xl p-4 shadow-2xl">
            <h3 className="font-bold text-gray-800 mb-1">🤖 {it ? "Sfida un avversario" : "Challenge an opponent"}</h3>
            <p className="text-xs text-gray-500 mb-3">{it ? "Dal più facile al più difficile: vinci per far salire il tuo punteggio!" : "From easiest to hardest: win to raise your rating!"}</p>
            <BotList bots={CHECKERS_BOTS} rating={child?.checkers_rating ?? 800} onPick={(b) => restart("ai", b)} testPrefix="checkers" />
            {others.length > 0 && (
              <button onClick={() => setPicking(true)} className="w-full py-4 mb-3 rounded-xl bg-gradient-to-r from-purple-600 to-pink-500 text-white font-bold text-lg hover:opacity-90" data-testid="checkers-vs-friend-btn">
                ⚔️ {it ? "Sfida un Amico" : "Challenge a Friend"}
              </button>
            )}
            <button onClick={() => restart("local")} className="w-full py-4 mb-3 rounded-xl bg-gradient-to-r from-stone-600 to-stone-700 text-white font-bold text-lg hover:opacity-90" data-testid="checkers-two-players-btn">
              👥 {it ? "2 Giocatori" : "2 Players"}
            </button>
          </div>
          {picking && (
            <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50" data-testid="checkers-opponent-picker">
              <div className="bg-white rounded-3xl p-6 max-w-sm w-full max-h-[80vh] overflow-y-auto">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-xl font-bold text-gray-800">⚔️ {it ? "Chi sfidi?" : "Who do you challenge?"}</h3>
                  <button onClick={() => setPicking(false)} className="text-gray-400 text-2xl" data-testid="close-opponent-picker">✕</button>
                </div>
                <div className="space-y-2">
                  {others.map((o) => (
                    <button
                      key={o.id}
                      onClick={() => { setOpponent(o); setPicking(false); restart("versus"); }}
                      className="w-full flex items-center gap-3 bg-purple-50 hover:bg-purple-100 border-2 border-purple-200 rounded-xl p-3 font-bold text-gray-700"
                      data-testid={`checkers-opponent-${o.id}`}
                    >
                      <span className="text-3xl">{o.avatar}</span>
                      <span>{o.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-800 via-stone-700 to-amber-900 p-4">
      <div className="max-w-lg mx-auto">
        <button onClick={() => setMode(null)} className="bg-white/20 text-white px-4 py-2 rounded-full font-bold hover:bg-white/30 mb-4" data-testid="checkers-back-btn">← {t.backHome}</button>

        <div className="text-center mb-3">
          {winner ? (
            <div className="bg-white rounded-2xl p-4 inline-block" data-testid="checkers-winner">
              <div className="text-3xl mb-1">{winner === 1 ? "🎉" : mode === "ai" ? bot.avatar : "🎉"}</div>
              <div className="font-bold text-stone-800">{it ? "Vince" : "Winner"}: {label(winner)}!</div>
              {reward && <div className="text-yellow-600 font-bold mt-1" data-testid="checkers-reward">{reward.text || `+${reward.coins_earned} 💰${reward.rating_after !== undefined ? ` · ${it ? "Punteggio" : "Rating"} ${reward.rating_after} (${reward.rating_delta >= 0 ? "+" : ""}${reward.rating_delta})` : ""}`}</div>}
            </div>
          ) : (
            <div className="text-white font-bold text-lg" data-testid="checkers-turn">
              {aiThinking ? `${bot.avatar} ${bot.name} ${it ? "pensa" : "is thinking"}... 🤔` : `${it ? "Tocca a" : "Turn"}: ${turn === 1 ? "🔴" : "⚫"} ${label(turn)}`}
            </div>
          )}
        </div>

        <div className="grid grid-cols-8 gap-0 rounded-2xl overflow-hidden shadow-2xl mx-auto border-4 border-amber-950" style={{ maxWidth: "440px" }} data-testid="checkers-board">
          {board.map((row, r) =>
            row.map((cell, c) => {
              const dark = (r + c) % 2 === 1;
              const isSel = selected && selected[0] === r && selected[1] === c;
              const isTarget = legalTargets.some(([tr, tc]) => tr === r && tc === c);
              return (
                <div
                  key={`${r}-${c}`}
                  onClick={() => dark && handleCell(r, c)}
                  data-testid={`checkers-cell-${r}-${c}`}
                  className={`aspect-square flex items-center justify-center ${dark ? "bg-amber-800" : "bg-amber-200"} ${isSel ? "ring-4 ring-yellow-400 ring-inset" : ""} ${isTarget ? "bg-green-600 cursor-pointer" : dark ? "cursor-pointer" : ""}`}
                >
                  {cell && (
                    <div className={`w-3/4 h-3/4 rounded-full shadow-lg flex items-center justify-center text-lg font-bold ${cell.p === 1 ? "bg-gradient-to-br from-red-500 to-red-700 text-red-100" : "bg-gradient-to-br from-stone-900 to-stone-700 text-stone-300"}`}>
                      {cell.k ? "👑" : ""}
                    </div>
                  )}
                  {isTarget && !cell && <div className="w-3 h-3 rounded-full bg-white/70"></div>}
                </div>
              );
            })
          )}
        </div>

        {winner && (
          <button onClick={() => restart(mode)} className="w-full mt-4 py-3 rounded-xl bg-white text-stone-800 font-bold hover:bg-gray-100" data-testid="checkers-restart-btn">
            🔄 {it ? "Gioca ancora" : "Play again"}
          </button>
        )}
        <p className="text-white/60 text-xs text-center mt-3">{it ? "Se puoi mangiare, devi mangiare! Arriva in fondo per fare Dama 👑" : "Captures are mandatory! Reach the last row to crown a King 👑"}</p>
      </div>
    </div>
  );
}
